/**
 * Pages/Panel/Empleados/rfc.js
 *
 * Cálculo del RFC de una persona física con el algoritmo del SAT (el de las reglas de las cuatro
 * letras, la homoclave del Anexo I/II y el dígito verificador del Anexo III). Lo usa la calculadora
 * del campo RFC (CalculadoraRfc.js) en Agregar Empleado, Editar Empleado y el alta por pasos.
 *
 * Los 13 caracteres salen de los datos: 4 letras, AAMMDD, homoclave y dígito verificador. Aun así
 * el SAT puede dar otra homoclave a dos personas con el mismo nombre, por eso un RFC calculado se
 * confirma con la constancia de situación fiscal o con la validación en SAT.
 *
 * Diferencias con la CURP (curp.js): el RFC conserva la Ñ, sin apellido materno usa las dos
 * primeras letras del paterno y del nombre (la CURP pone X), con un paterno de una o dos letras
 * cambia la regla, y en una palabra inconveniente cambia la ÚLTIMA letra (la CURP, la segunda).
 *
 * Módulo puro (sin React ni store): se puede probar con un script de Node.
 */

import { PARTICULAS, NOMBRES_QUE_SE_BRINCAN, leerFecha } from "./curp";

/** Tramos del RFC, para pintarlo separado en la calculadora. */
export const SEGMENTOS_RFC = [
  { desde: 0, hasta: 4, titulo: "Iniciales" },
  { desde: 4, hasta: 10, titulo: "Nacimiento" },
  { desde: 10, hasta: 12, titulo: "Homoclave" },
  { desde: 12, hasta: 13, titulo: "Verificador" },
];

// Anexo IV del SAT: si las cuatro letras forman una de estas palabras, la última se cambia por X.
const INCONVENIENTES = new Set([
  "BUEI", "BUEY", "CACA", "CACO", "CAGA", "CAGO", "CAKA", "CAKO", "COGE", "COJA", "COJE", "COJI",
  "COJO", "CULO", "FETO", "GUEY", "JOTO", "KACA", "KACO", "KAGA", "KAGO", "KAKA", "KOGE", "KOJO",
  "KULO", "MAME", "MAMO", "MEAR", "MEAS", "MEON", "MION", "MOCO", "MULA", "PEDA", "PEDO", "PENE",
  "PUTA", "PUTO", "QULO", "RATA", "RUIN",
]);

// Anexo I: valor de cada carácter del nombre para la homoclave.
const VALORES_HOMOCLAVE = { " ": "00", "&": "10", "Ñ": "40" };
"0123456789".split("").forEach((c, i) => { VALORES_HOMOCLAVE[c] = `0${i}`; });
"ABCDEFGHI".split("").forEach((c, i) => { VALORES_HOMOCLAVE[c] = String(11 + i); });
"JKLMNOPQR".split("").forEach((c, i) => { VALORES_HOMOCLAVE[c] = String(21 + i); });
"STUVWXYZ".split("").forEach((c, i) => { VALORES_HOMOCLAVE[c] = String(32 + i); });

// Anexo II: los 34 caracteres de la homoclave (sin el 0 ni la O).
const CARACTERES_HOMOCLAVE = "123456789ABCDEFGHIJKLMNPQRSTUVWXYZ";

// Anexo III: el valor de cada carácter para el dígito verificador es su posición aquí.
const DICCIONARIO_DIGITO = "0123456789ABCDEFGHIJKLMN&OPQRSTUVWXYZ Ñ";
const DIACRITICOS = /[̀-ͯ]/g;

// Mayúsculas y sin acentos, pero con la Ñ: NFD la parte en N + tilde y aquí se vuelve a juntar
// antes de quitar los acentos. Guiones y diagonales separan palabras; los demás símbolos (puntos
// de "Ma.", apóstrofos) se quitan.
const palabras = (texto) => String(texto ?? "")
  .toUpperCase()
  .normalize("NFD")
  .replace(/Ñ/g, "Ñ")
  .replace(DIACRITICOS, "")
  .replace(/[-/]/g, " ")
  .replace(/[^A-ZÑ& ]/g, "")
  .split(/\s+/)
  .filter(Boolean);

// La palabra que cuenta de un apellido: la primera que no es partícula.
const palabraApellido = (texto) => {
  const todas = palabras(texto);
  return todas.find((p) => !PARTICULAS.has(p)) || todas[0] || "";
};

// La palabra que cuenta del nombre: sin partículas, y brincando María/José si hay otro nombre.
const palabraNombre = (texto) => {
  const todas = palabras(texto);
  const utiles = todas.filter((p) => !PARTICULAS.has(p));
  if (utiles.length > 1 && NOMBRES_QUE_SE_BRINCAN.has(utiles[0])) return utiles[1];
  return utiles[0] || todas[0] || "";
};

const primeraVocalInterna = (palabra) => {
  for (let i = 1; i < palabra.length; i++) if ("AEIOU".includes(palabra[i])) return palabra[i];
  return "X";
};

const dosLetras = (palabra) => `${palabra}XX`.slice(0, 2);

/** Homoclave (posiciones 11 y 12) del nombre completo "PATERNO MATERNO NOMBRE", ya normalizado. */
export function homoclaveRfc(nombreCompleto) {
  const digitos = "0" + [...String(nombreCompleto ?? "")].map((c) => VALORES_HOMOCLAVE[c] ?? "00").join("");
  let suma = 0;
  for (let i = 0; i < digitos.length - 1; i++) suma += Number(digitos.slice(i, i + 2)) * Number(digitos[i + 1]);
  const n = suma % 1000;
  return CARACTERES_HOMOCLAVE[Math.floor(n / 34)] + CARACTERES_HOMOCLAVE[n % 34];
}

/** Dígito verificador (posición 13) de los primeros 12 caracteres. */
export function digitoVerificadorRfc(rfc12) {
  let suma = 0;
  for (let i = 0; i < 12; i++) suma += DICCIONARIO_DIGITO.indexOf(rfc12[i]) * (13 - i);
  const residuo = suma % 11;
  if (residuo === 0) return "0";
  return residuo === 1 ? "A" : String(11 - residuo);
}

/**
 * Calcula el RFC. Regresa `{ rfc, faltan }`: sin todos los datos, `rfc` es null y `faltan` dice
 * cuáles.
 */
export function calcularRfc({ nombre, apellidoPaterno, apellidoMaterno, fechaNacimiento } = {}) {
  const paterno = palabraApellido(apellidoPaterno);
  const materno = palabraApellido(apellidoMaterno);
  const nombrePila = palabraNombre(nombre);
  const fecha = leerFecha(fechaNacimiento);

  const faltan = [];
  if (!nombrePila) faltan.push("Nombre(s)");
  if (!paterno) faltan.push("Apellido paterno");
  if (!fecha) faltan.push("Fecha de nacimiento");
  if (faltan.length) return { rfc: null, faltan };

  let letras;
  if (!materno) letras = dosLetras(paterno) + dosLetras(nombrePila);
  else if (paterno.length <= 2) letras = paterno[0] + materno[0] + dosLetras(nombrePila);
  else letras = paterno[0] + primeraVocalInterna(paterno) + materno[0] + nombrePila[0];
  if (INCONVENIENTES.has(letras)) letras = letras.slice(0, 3) + "X";

  // La homoclave va con el nombre completo tal cual, partículas y María/José incluidos.
  const nombreCompleto = [...palabras(apellidoPaterno), ...palabras(apellidoMaterno), ...palabras(nombre)].join(" ");
  const rfc12 = letras + fecha.format("YYMMDD") + homoclaveRfc(nombreCompleto);

  return { rfc: rfc12 + digitoVerificadorRfc(rfc12), faltan };
}

/** ¿Los dos RFC son de los mismos datos? Compara los primeros 10 caracteres (sin la homoclave). */
export const mismoRfcBase = (a, b) => {
  const x = String(a ?? "").trim().toUpperCase();
  const y = String(b ?? "").trim().toUpperCase();
  return x.length === 13 && y.length === 13 && x.slice(0, 10) === y.slice(0, 10);
};
